import { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card } from '@/components/ui/card';
import { parseISODate } from '@/utils/helpers';
import { LoanValuation } from '@/services/portfolio.service';

interface MaturityAnalysisChartProps {
  loans: LoanValuation[];
}

interface MaturityData {
  period: string;
  month: Date;
  balance: number;
  count: number;
  loans: LoanValuation[];
}

export function MaturityAnalysisChart({ loans }: MaturityAnalysisChartProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<MaturityData | null>(
    null
  );

  const maturityData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Нормализуем время для корректного сравнения
    const periods: MaturityData[] = [];

    // Отладка: проверяем входящие данные
    if (loans.length > 0) {
      console.log('MaturityChart: Total loans received:', loans.length);
      const sampleDates = loans.slice(0, 5).map((l) => ({
        loan_name: l.loan_name,
        maturity_date: l.effective_maturity_date,
        balance: l.current_balance,
        parsed_date: l.effective_maturity_date
          ? parseISODate(l.effective_maturity_date).toISOString()
          : 'invalid',
      }));
      console.log('MaturityChart: Sample loan dates:', sampleDates);

      // Находим минимальную и максимальную даты maturity
      const validDates = loans
        .map((l) =>
          l.effective_maturity_date ? parseISODate(l.effective_maturity_date) : null
        )
        .filter((d) => d && !isNaN(d.getTime()));
      if (validDates.length > 0) {
        const minDate = new Date(
          Math.min(...validDates.map((d) => d?.getTime() ?? 0))
        );
        const maxDate = new Date(
          Math.max(...validDates.map((d) => d?.getTime() ?? 0))
        );
        console.log('MaturityChart: Date range:', {
          min: minDate.toISOString(),
          max: maxDate.toISOString(),
          today: today.toISOString(),
        });
      }
    }

    // Next 6 months (monthly)
    for (let i = 0; i < 6; i++) {
      const month = new Date(today.getFullYear(), today.getMonth() + i + 1, 1);
      const monthEnd = new Date(
        today.getFullYear(),
        today.getMonth() + i + 2,
        0
      );
      monthEnd.setHours(23, 59, 59, 999); // Конец дня

      const periodLoans = loans.filter((loan) => {
        if (!loan.effective_maturity_date) return false;
        const maturityDate = parseISODate(loan.effective_maturity_date);
        if (isNaN(maturityDate.getTime())) return false;
        maturityDate.setHours(0, 0, 0, 0);
        return maturityDate >= month && maturityDate <= monthEnd;
      });

      const balance = periodLoans.reduce(
        (sum, loan) => sum + (loan.current_balance || 0),
        0
      );

      periods.push({
        period: month.toLocaleDateString('en-US', {
          month: 'short',
          year: '2-digit',
        }),
        month,
        balance,
        count: periodLoans.length,
        loans: periodLoans,
      });
    }

    // Next quarters (7-24 months)
    for (let q = 0; q < 6; q++) {
      const quarterStart = new Date(
        today.getFullYear(),
        today.getMonth() + 7 + q * 3,
        1
      );
      const quarterEnd = new Date(
        today.getFullYear(),
        today.getMonth() + 10 + q * 3,
        0
      );
      quarterEnd.setHours(23, 59, 59, 999); // Конец дня

      const periodLoans = loans.filter((loan) => {
        if (!loan.effective_maturity_date) return false;
        const maturityDate = parseISODate(loan.effective_maturity_date);
        if (isNaN(maturityDate.getTime())) return false;
        maturityDate.setHours(0, 0, 0, 0);
        return maturityDate >= quarterStart && maturityDate <= quarterEnd;
      });

      const balance = periodLoans.reduce(
        (sum, loan) => sum + (loan.current_balance || 0),
        0
      );

      const quarterName = `Q${Math.floor((quarterStart.getMonth() + 1 - 1) / 3) + 1} ${quarterStart.getFullYear()}`;

      periods.push({
        period: quarterName,
        month: quarterStart,
        balance,
        count: periodLoans.length,
        loans: periodLoans,
      });
    }

    // Добавляем годовые периоды для дальних дат (25+ месяцев до 10 лет вперед)
    const yearsToShow = 10;
    for (let year = 2; year <= yearsToShow; year++) {
      const yearStart = new Date(today.getFullYear() + year, 0, 1);
      const yearEnd = new Date(today.getFullYear() + year, 11, 31);
      yearEnd.setHours(23, 59, 59, 999);

      const periodLoans = loans.filter((loan) => {
        if (!loan.effective_maturity_date) return false;
        const maturityDate = parseISODate(loan.effective_maturity_date);
        if (isNaN(maturityDate.getTime())) return false;
        maturityDate.setHours(0, 0, 0, 0);
        return maturityDate >= yearStart && maturityDate <= yearEnd;
      });

      const balance = periodLoans.reduce(
        (sum, loan) => sum + (loan.current_balance || 0),
        0
      );

      // Добавляем только если есть займы в этом году
      if (balance > 0 || periodLoans.length > 0) {
        periods.push({
          period: `${today.getFullYear() + year}`,
          month: yearStart,
          balance,
          count: periodLoans.length,
          loans: periodLoans,
        });
      }
    }

    // Отладка: проверяем итоговые данные
    const totalBalance = periods.reduce((sum, p) => sum + p.balance, 0);
    const totalCount = periods.reduce((sum, p) => sum + p.count, 0);
    console.log('MaturityChart: Periods created:', periods.length);
    console.log(
      'MaturityChart: Total balance across all periods:',
      totalBalance
    );
    console.log('MaturityChart: Total loans in periods:', totalCount);
    console.log(
      'MaturityChart: Periods with data:',
      periods.filter((p) => p.balance > 0).length
    );

    return periods;
  }, [loans]);

  const handleBarClick = (data: MaturityData) => {
    setSelectedPeriod(data);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatTooltipValue = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    return formatCurrency(value);
  };

  return (
    <div className="space-y-6" data-test-id="maturity-analysis-chart-container">
      <Card className="p-6" data-test-id="maturity-analysis-chart">
        <h3
          className="text-lg font-semibold text-gray-900 mb-4"
          data-test-id="chart-title"
        >
          Maturity Analysis
        </h3>
        <div className="h-80" data-test-id="chart-wrapper">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={maturityData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="opacity-50" />
              <XAxis
                dataKey="period"
                angle={-45}
                textAnchor="end"
                height={60}
                style={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatTooltipValue}
                style={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                labelFormatter={(label) => `Period: ${label}`}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                }}
              />
              <Bar
                dataKey="balance"
                name="Maturing Balance"
                onClick={(data: MaturityData) => {
                  handleBarClick(data);
                }}
                className="cursor-pointer"
              >
                {maturityData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.balance > 0 ? '#3b82f6' : '#e5e7eb'}
                    className="hover:opacity-80 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBarClick(entry);
                    }}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p
          className="text-sm text-gray-600 mt-4 text-center"
          data-test-id="chart-description"
        >
          Click on a bar to see loan details for that period
        </p>
      </Card>

      {/* Selected Period Details */}
      {selectedPeriod && selectedPeriod.count > 0 && (
        <Card className="p-6" data-test-id="selected-period-details">
          <div className="flex justify-between items-center mb-4">
            <h4
              className="text-lg font-semibold text-gray-900"
              data-test-id="selected-period-title"
            >
              Maturing Loans - {selectedPeriod.period}
            </h4>
            <button
              onClick={() => setSelectedPeriod(null)}
              className="text-sm text-gray-500 hover:text-gray-700"
              data-test-id="close-details-button"
            >
              Close
            </button>
          </div>

          <div
            className="mb-4 grid grid-cols-2 gap-4"
            data-test-id="period-summary"
          >
            <div>
              <p
                className="text-sm text-gray-600"
                data-test-id="total-balance-label"
              >
                Total Balance
              </p>
              <p
                className="text-xl font-semibold text-gray-900"
                data-test-id="total-balance-value"
              >
                {formatCurrency(selectedPeriod.balance)}
              </p>
            </div>
            <div>
              <p
                className="text-sm text-gray-600"
                data-test-id="loan-count-label"
              >
                Number of Loans
              </p>
              <p
                className="text-xl font-semibold text-gray-900"
                data-test-id="loan-count-value"
              >
                {selectedPeriod.count}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto" data-test-id="loans-table-container">
            <table
              className="min-w-full divide-y divide-gray-200"
              data-test-id="loans-table"
            >
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loan Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Borrower
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Maturity Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Interest Type
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedPeriod.loans.map((loan) => (
                  <tr
                    key={loan.loan_id}
                    className="hover:bg-gray-50"
                    data-test-id={`loan-row-${loan.loan_id}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {loan.loan_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {loan.sponsor_borrower}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(loan.current_balance)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {new Date(
                        loan.effective_maturity_date
                      ).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {loan.interest_type}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
